/**
 * Responsibility: the Alerts page data and the mutations behind it.
 *
 * Creating and rotating both return a credential that the server will not hand
 * back afterwards, so those mutations deliberately return their result to the
 * caller rather than only invalidating the list - the page has to show the
 * topic once and cannot re-fetch it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hubApi } from '../client';
import type { SubscriptionCreate, SubscriptionPatch } from '../client';

export function useSubscriptions() {
  return useQuery({ queryKey: ['subscriptions'], queryFn: hubApi.listSubscriptions });
}

export function useNotifyLists() {
  return useQuery({ queryKey: ['notify-lists'], queryFn: hubApi.listNotifyLists });
}

export function useCreateSubscription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: SubscriptionCreate) => hubApi.createSubscription(payload),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['subscriptions'] });
      // A channel can name a list that no product uses yet.
      client.invalidateQueries({ queryKey: ['notify-lists'] });
    },
  });
}

export function useRotateSubscription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hubApi.rotateSubscription(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['subscriptions'] });
      // A channel can name a list that no product uses yet.
      client.invalidateQueries({ queryKey: ['notify-lists'] });
    },
  });
}

export function usePatchSubscription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: SubscriptionPatch }) =>
      hubApi.patchSubscription(id, patch),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['subscriptions'] });
      // A channel can name a list that no product uses yet.
      client.invalidateQueries({ queryKey: ['notify-lists'] });
    },
  });
}

export function useTestSubscription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hubApi.testSubscription(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['subscriptions'] });
      // A channel can name a list that no product uses yet.
      client.invalidateQueries({ queryKey: ['notify-lists'] });
    },
  });
}

export function useDeleteSubscription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hubApi.deleteSubscription(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['subscriptions'] });
      // A channel can name a list that no product uses yet.
      client.invalidateQueries({ queryKey: ['notify-lists'] });
    },
  });
}
